/**
 * Centralized Access Control Utility
 * Handles all feature access logic based on subscription status
 */

/**
 * Check if user can access MCQ at given index
 * @param {number} index - 0-based index of MCQ technology
 * @param {boolean} isPaid - Whether user has paid subscription
 * @returns {boolean}
 */
export const canAccessMCQ = (index, isPaid) => {
    if (isPaid) return true;
    return index < 5; // Free users: first 5 technologies only (indices 0-4)
};

/**
 * Check if user can access Documentation at given index
 * @param {number} index - 0-based index of documentation file
 * @param {boolean} isPaid - Whether user has paid subscription
 * @returns {boolean}
 */
export const canAccessDoc = (index, isPaid) => {
    if (isPaid) return true;
    return index < 2; // Free users: first 2 documents only (indices 0-1)
};

/**
 * Check if user can access TSG Notes
 * @param {boolean} isPaid - Whether user has paid subscription
 * @returns {boolean}
 */
export const canAccessTSGNotes = (isPaid) => {
    return isPaid; // Paid users only, no trial, no preview
};

/**
 * Check if user can access BootCamp
 * @param {boolean} isPaid - Whether user has paid subscription
 * @returns {boolean}
 */
export const canAccessBootCamp = (isPaid) => {
    return isPaid; // Paid users only, no trial, no preview
};

/**
 * Check if user can start a new mock interview
 * @param {number} completedCount - Number of completed interviews
 * @param {boolean} isPaid - Whether user has paid subscription
 * @returns {boolean}
 */
export const canAccessMockInterview = (completedCount, isPaid) => {
    if (isPaid) return true;
    return completedCount < 1; // Free users: 1 attempt only
};

/**
 * Check if user has TSG AI access (3 or 6 month subscribers only)
 * @param {boolean} hasAiAccess - From subscription data
 * @returns {boolean}
 */
export const canAccessTSGAI = (hasAiAccess) => {
    return hasAiAccess;
};

/**
 * Check if user can access Elite Technology document at given index
 * Only applies after user has selected a technology
 * @param {number} index - 0-based index of elite document
 * @param {boolean} isPaid - Whether user has paid subscription
 * @returns {boolean}
 */
export const canAccessEliteDoc = (index, isPaid) => {
    if (isPaid) return true;
    return index < 2; // Free users: first 2 documents only
};

/**
 * Check if technology is accessible for free user in Elite
 * @param {string} technologyId - Technology identifier (e.g., 'java-premium')
 * @param {string|null} selectedTechnology - User's selected technology
 * @param {boolean} isPaid - Whether user has paid subscription
 * @returns {boolean}
 */
export const canViewEliteTechnology = (technologyId, selectedTechnology, isPaid) => {
    if (isPaid) return true;
    if (!selectedTechnology) return false; // Must select first
    return technologyId === selectedTechnology;
};

/**
 * Get premium upgrade message based on feature
 * @param {string} feature - Feature name
 * @returns {object} {title, message}
 */
export const getPremiumMessage = (feature) => {
    const messages = {
        mcq: {
            title: "Unlock All MCQs",
            message: "Upgrade to access MCQs for all technologies. Free users can practice only the first 5 technologies."
        },
        doc: {
            title: "Unlock Full Documentation",
            message: "Upgrade to access all documentation. Free users can view only the first 2 documents per technology."
        },
        tsgNotes: {
            title: "TSG Notes - Premium Only",
            message: "TSG Notes is available only for premium subscribers. Upgrade now to take smart notes with AI assistance."
        },
        bootcamp: {
            title: "BootCamp Access Required",
            message: "BootCamp access is available only for subscribed users. Upgrade to start your structured learning journey."
        },
        mockInterview: {
            title: "Mock Interview Limit Reached",
            message: "You have already used your free mock interview attempt. Upgrade to continue practicing with unlimited interviews."
        },
        tsgAI: {
            title: "TSG AI - Premium Feature",
            message: "TSG AI is available only for 3-month and 6-month subscribers. Upgrade to unlock AI-powered assistance."
        },
        elite: {
            title: "Elite Technology Mastery - Upgrade Required",
            message: "Upgrade your subscription to unlock all Elite Technologies and access all premium documentation without limits."
        }
    };

    return messages[feature] || {
        title: "Unlock Premium Features",
        message: "Upgrade your subscription to continue learning without limits."
    };
};
